import React from 'react'
import { useTranslation } from 'react-i18next'

import { useMarketplace } from '@src/hooks/useMarketplace'

import {
  Address,
  AppButtonContainer,
  AppButtonWrapper,
  ButtonsContainer,
  ContactContainer,
  ContactEmail,
  ContactText,
  Container,
  PhoneTextLink,
  PolicyContainer,
  SocialContainer,
  StyledAppStore,
  StyledATag,
  StyledFacebookIconSVG,
  StyledIconWrapper,
  StyledInstagramIconSVG,
  StyledLink,
  StyledLinkWrapper,
  StyledNewTabLink,
  StyledPlayStore,
  StyledTikTokIconSVG,
  StyledTwitterXIconSVG,
  Title,
} from './Footer.styles'
import { useNavigate } from 'react-router-dom'

export const Footer: React.FC = () => {
  const { t } = useTranslation('footer')
  const navigate = useNavigate()

  const {
    supportEmail,
    supportPhone,
    socialURLInstagram,
    socialURLFacebook,
    socialURLTwitter,
    socialURLTikTok,
    storeURLApple,
    storeURLGooglePlay,
    vatNumber,
    companyNumber,
    contactAddress,
    footerLinks,
    companyLegalName,
    legalAllergy,
    legalPolicy,
    legalTerms,
    legalPrivacy,
    legalCookies,
  } = useMarketplace()

  const { firstLine, secondLine, thirdLine, city, postcode, country } =
    contactAddress

  const addressArray = [
    firstLine,
    secondLine,
    thirdLine,
    city,
    postcode,
    country && country.name,
  ]

  return (
    <Container>
      <ContactContainer>
        <Title>{companyLegalName}</Title>
        <Address>
          {addressArray.map(
            (item, index) =>
              item &&
              item.length > 0 && (
                <div key={item}>
                  {item}
                  {index + 1 !== addressArray.length && (
                    <>
                      {`,`}
                      <br />
                    </>
                  )}
                </div>
              )
          )}
        </Address>
        {companyNumber && (
          <ContactText>
            {t('company_no')}
            {companyNumber}
          </ContactText>
        )}
        {vatNumber && (
          <ContactText>
            {t('vat_no')}
            {vatNumber}
          </ContactText>
        )}

        {(supportEmail || supportPhone) && <Title>{t('support')}</Title>}
        {supportEmail && (
          <ContactText>
            {t('support_email')}:<br />
            <ContactEmail href={`mailto:${supportEmail}`}>
              {supportEmail}
            </ContactEmail>
          </ContactText>
        )}
        {supportPhone && (
          <ContactText>
            {t('support_phone')}:<br />
            <PhoneTextLink href={`tel:${supportPhone}`}>
              {supportPhone}
            </PhoneTextLink>
          </ContactText>
        )}
      </ContactContainer>
      {(socialURLInstagram ||
        socialURLFacebook ||
        socialURLTwitter ||
        socialURLTikTok) && (
        <SocialContainer>
          <Title>{t('title_social')}</Title>
          {socialURLInstagram && (
            <StyledLinkWrapper>
              <StyledNewTabLink href={socialURLInstagram}>
                <StyledIconWrapper>
                  <StyledInstagramIconSVG
                    className={'instagram-icon'}
                    data-test-id="footer-socialmedia-link-instagram"
                  />
                </StyledIconWrapper>
                {t('instagram')}
              </StyledNewTabLink>
            </StyledLinkWrapper>
          )}
          {socialURLFacebook && (
            <StyledLinkWrapper>
              <StyledNewTabLink href={socialURLFacebook}>
                <StyledIconWrapper>
                  <StyledFacebookIconSVG
                    className={'facebook-icon'}
                    data-test-id="footer-socialmedia-link-facebook"
                  />
                </StyledIconWrapper>
                {t('facebook')}
              </StyledNewTabLink>
            </StyledLinkWrapper>
          )}
          {socialURLTwitter && (
            <StyledLinkWrapper>
              <StyledNewTabLink href={socialURLTwitter}>
                <StyledIconWrapper>
                  <StyledTwitterXIconSVG
                    className={'twitter-X-icon'}
                    data-test-id="footer-socialmedia-link-twitter"
                  />
                </StyledIconWrapper>
                {t('twitter')}
              </StyledNewTabLink>
            </StyledLinkWrapper>
          )}
          {socialURLTikTok && (
            <StyledLinkWrapper>
              <StyledNewTabLink href={socialURLTikTok}>
                <StyledIconWrapper>
                  <StyledTikTokIconSVG
                    className={'tik-tok-icon'}
                    data-test-id="footer-socialmedia-link-tiktok"
                  />
                </StyledIconWrapper>
                {t('tiktok')}
              </StyledNewTabLink>
            </StyledLinkWrapper>
          )}
        </SocialContainer>
      )}
      <PolicyContainer>
        <Title>{t('title_legal')}</Title>
        {legalTerms && (
          <StyledLink
            onClick={() => navigate('?account=terms')}
            data-test-id="footer-legal&help-link-termsandconditions"
          >
            {t('terms')}
          </StyledLink>
        )}
        {legalPrivacy && (
          <StyledLink
            onClick={() => navigate('?account=privacy')}
            data-test-id="footer-legal&help-link-privacypolicy"
          >
            {t('privacy')}
          </StyledLink>
        )}
        {legalAllergy && (
          <StyledLink
            onClick={() => navigate('?account=allergy')}
            data-test-id="footer-legal&help-link-allergypolicy"
          >
            {t('allergy')}
          </StyledLink>
        )}
        {legalCookies && (
          <StyledLink
            onClick={() => navigate('?account=cookies')}
            data-test-id="footer-legal&help-link-cookies"
          >
            {t('cookies')}
          </StyledLink>
        )}
        {legalPolicy && (
          <StyledLink
            onClick={() => navigate('?account=misc')}
            data-test-id="footer-legal&help-link-otherpolicies"
          >
            {t('misc_policies')}
          </StyledLink>
        )}
        <StyledLink
          onClick={() => navigate('?account=help&accountChild=support')}
          data-test-id="footer-legal&help-link-helpandsupport"
        >
          {t('help')}
        </StyledLink>
        {footerLinks.map(footerLink => (
          <StyledATag
            key={footerLink.id}
            href={footerLink.link}
            target="_blank"
          >
            {footerLink.title}
          </StyledATag>
        ))}
      </PolicyContainer>
      <ButtonsContainer>
        {(storeURLApple || storeURLGooglePlay) && (
          <>
            <Title>{t('title_download')}</Title>
            <AppButtonWrapper>
              {storeURLApple && (
                <AppButtonContainer href={storeURLApple}>
                  <StyledAppStore
                    id={t('download')}
                    data-test-id="footer-apps-icon-appstore"
                  />
                </AppButtonContainer>
              )}
              {storeURLGooglePlay && (
                <AppButtonContainer href={storeURLGooglePlay}>
                  <StyledPlayStore
                    id={t('playStore')}
                    data-test-id="footer-apps-icon-googleplay"
                  />
                </AppButtonContainer>
              )}
            </AppButtonWrapper>
          </>
        )}
      </ButtonsContainer>
    </Container>
  )
}
